import isPropValid from "@emotion/is-prop-valid";
import { IStyleSheetContext } from "styled-components";

import {
    ENV_EVALA_PRODUCTION,
    ENV_EVALA_VERSION,
    ENV_LOCAL_DEV_MODE,
    LOGIN_PAGE_SUBDOMAIN,
    SYSTEM_CONFIG_URL
} from "./constants";
import memoize from "./utils/memoize";

interface ISystemConfiguration {
    DisableSubdomains?: boolean;
    Domain?: string;
    InboxEmailDomain?: string;
}

export const getSystemConfig = memoize(async (): Promise<ISystemConfiguration> => {
    const result = await fetch(SYSTEM_CONFIG_URL, { method: "GET" });
    if (!result.ok) {
        // todo: what in this case...
        return {};
    }
    return await result.json();
});

/** This will only return true in actual production, not on nightly.
 * It differs from process.env.NODE_ENV which returns "development" when running dev server and "production" on build (nightly) */
export const isEvalaProduction = (): boolean => {
    return !!process.env[ENV_EVALA_PRODUCTION];
};

export const isLocalDevMode = (): boolean => {
    return !!process.env[ENV_LOCAL_DEV_MODE];
};

export const isLoginAppSubdomain = (): boolean => {
    const { host } = window.location;
    return host.startsWith(`${LOGIN_PAGE_SUBDOMAIN}.`);
};

export const EvalaDevelVersion = "devel";

/** Returns current Evala version in production, "devel" in development. */
export const getEvalaVersion = (): string => {
    return process.env[ENV_EVALA_VERSION] || EvalaDevelVersion;
};

// This implements the default behavior from styled-components v5
export const shouldForwardProp: IStyleSheetContext['shouldForwardProp'] = (propName, target) => {
    if (typeof target === "string") {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
};

export function isSafari(): boolean {
    const ua = window.navigator.userAgent;
    return /^((?!chrome|android|crios|fxios|edgios|edg).)*safari/i.test(ua);
}

export function isFirefox(): boolean {
    const ua = navigator.userAgent;
    return /firefox/i.test(ua) || /fxios/i.test(ua);
}

export class RateLimiter<T extends unknown[] = unknown[], R = unknown> {
    private limit: number;
    private interval: number;
    private onLimitExceeded: () => R;
    private calls: number[];

    constructor(limit: number, interval: number, onLimitExceeded: () => R) {
        this.limit = limit;
        this.interval = interval;
        this.onLimitExceeded = onLimitExceeded;
        this.calls = [];
    }

    call(fn: (...fArgs: T) => R, ...args: T): R {
        const now = Date.now();
        this.calls = this.calls.filter(ts => now - ts < this.interval);

        if (this.calls.length >= this.limit) {
            if (this.onLimitExceeded) {
                return this.onLimitExceeded();
            } else {
                throw new Error("Rate limit exceeded");
            }
        }

        this.calls.push(now);
        return fn(...args);
    }
}

/**
 * In case condition is true, returns opts in array as is, otherwise in reversed order. So you can call it like:
 * const [first, another] = pickPairBy(condition, option1, option2);
 * and get option1 in first and option2 in another if condition is true, otherwise the opposite.
 * @param condition
 * @param opts
 */
export function pickPairBy<T>(condition: boolean, ...opts: T[]): T[] {
    if (!condition) {
        return opts.reverse();
    }
    return opts;
}
